import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export default function Footer() {
  const { t } = useLanguage();
  
  const categories = [
    { id: "cars", name: t('categories.cars') },
    { id: "real_estate", name: t('categories.real_estate') },
    { id: "electronics", name: t('categories.electronics') },
    { id: "jobs", name: t('categories.jobs') },
    { id: "services", name: t('categories.services') },
    { id: "fashion", name: t('categories.fashion') },
  ];

  return (
    <footer className="bg-[#0A0A0A] border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white font-bold text-lg">X</span>
              </div>
              <div>
                <span className="text-xl font-bold text-white">X67</span>
                <span className="text-xs text-slate-500 block -mt-1">Digital Media Groupe</span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {t('footerDescription')}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/profile.php?id=61588030305324"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X67 Digital pe Facebook"
                data-testid="footer-facebook"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-blue-600/20 transition-colors"
              >
                <Facebook className="w-5 h-5 text-slate-400" />
              </a>
              <a
                href="https://www.instagram.com/x67digital"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X67 Digital pe Instagram"
                data-testid="footer-instagram"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-pink-600/20 transition-colors"
              >
                <Instagram className="w-5 h-5 text-slate-400" />
              </a>
              <a
                href="https://twitter.com/x67digital"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X67 Digital pe X/Twitter"
                data-testid="footer-twitter"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-blue-400/20 transition-colors"
              >
                <Twitter className="w-5 h-5 text-slate-400" />
              </a>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-white font-semibold mb-6">{t('popularCategories')}</h3>
            <ul className="space-y-3">
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link 
                    to={`/category/${cat.id}`} 
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-white font-semibold mb-6">{t('usefulLinks')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/create-ad" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('createAd')}
                </Link>
              </li>
              <li>
                <Link to="/sellers" className="text-amber-300 hover:text-amber-200 transition-colors text-sm inline-flex items-center gap-1.5 font-medium">
                  <span className="text-base">★</span>
                  Top Vânzători
                </Link>
              </li>
              <li>
                <Link to="/termeni-si-conditii" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('termsConditions')}
                </Link>
              </li>
              <li>
                <Link to="/politica-confidentialitate" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/intrebari-frecvente" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('faq')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('blog')} & {t('guides')}
                </Link>
              </li>
              <li>
                <Link to="/stories" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('stories')}
                </Link>
              </li>
              <li>
                <Link to="/forum" className="text-slate-400 hover:text-white transition-colors text-sm">
                  {t('communityForum')}
                </Link>
              </li>
              <li>
                <Link to="/sistem" className="text-emerald-300 hover:text-white transition-colors text-sm inline-flex items-center gap-1.5">
                  <span className="relative inline-flex w-2 h-2">
                    <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" />
                    <span className="absolute inset-0 rounded-full bg-emerald-400" />
                  </span>
                  Status
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-500 mt-0.5" />
                <span className="text-slate-400 text-sm">contact@x67digital.com</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-blue-500 mt-0.5" />
                <span className="text-slate-400 text-sm">0730 268 067</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-500 mt-0.5" />
                <span className="text-slate-400 text-sm">București, România</span>
              </li>
            </ul>
          </div>
        </div>

        {/* SEO Internal Links */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <h3 className="text-white font-semibold mb-4">Anunturi populare pe orase</h3>
          <div className="flex flex-wrap gap-2">
            {[
              { cat: "auto", city: "bucuresti", label: "Auto Bucuresti" },
              { cat: "imobiliare", city: "bucuresti", label: "Imobiliare Bucuresti" },
              { cat: "auto", city: "cluj-napoca", label: "Auto Cluj-Napoca" },
              { cat: "imobiliare", city: "cluj-napoca", label: "Imobiliare Cluj" },
              { cat: "electronice", city: "bucuresti", label: "Electronice Bucuresti" },
              { cat: "auto", city: "timisoara", label: "Auto Timisoara" },
              { cat: "locuri-de-munca", city: "bucuresti", label: "Joburi Bucuresti" },
              { cat: "imobiliare", city: "timisoara", label: "Imobiliare Timisoara" },
              { cat: "servicii", city: "bucuresti", label: "Servicii Bucuresti" },
              { cat: "auto", city: "iasi", label: "Auto Iasi" },
              { cat: "imobiliare", city: "iasi", label: "Imobiliare Iasi" },
              { cat: "electronice", city: "cluj-napoca", label: "Electronice Cluj" },
            ].map((link) => (
              <Link
                key={`${link.cat}-${link.city}`}
                to={`/anunturi/${link.cat}/${link.city}`}
                className="text-xs text-slate-500 hover:text-blue-400 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} X67 Digital Media Groupe. {t('allRightsReserved')}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 text-sm">{t('securePayments')}</span>
            <span className="text-blue-500 font-medium">Viva Wallet</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
